import { useQuery } from '@apollo/client';
import { useState } from 'react';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { graphql } from '~/apollo/generated/v4';
import { Heading } from '~/components/common/Heading';
import { NotFound } from '~/components/common/NotFound';
import { Panel } from '~/components/common/Panel';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { KeyParametersPanelV4 } from '~/components/key-parameters/key-parameters-panel-v4';
import { StudyQualityParametersTable } from '~/components/study/study-quality-parameters-table';
import { useRouteParam } from '~/hooks/routing';

const STUDY_KEY_PARAMETERS_PAGE = graphql(`
  query StudyKeyParametersPage($id: ID!) {
    studyGet(id: $id) {
      id
      dominantGeologyType
      dataAcquisitionMethod
      publicationType
      spatialObservationType
      keyParameters {
        ...keyParametersParts
      }
      architecturalElements {
        ...keyParametersFromAeParts
      }
      outcrops {
        id
        keyParameters {
          ...keyParametersParts
        }
      }
    }
  }
`);

export default function StudyKeyParametersPage() {
  return (
    <ApolloProviderV4>
      <StudyKeyParametersPageInner />
    </ApolloProviderV4>
  );
}

function StudyKeyParametersPageInner() {
  const studyId = useRouteParam('studyId', parseInt);
  const [includeAE, setIncludeAE] = useState(true);
  const [includeOC, setIncludeOC] = useState(true);

  const { data, loading } = useQuery(STUDY_KEY_PARAMETERS_PAGE, {
    variables: { id: studyId },
  });

  const study = data?.studyGet;

  if (loading) return <SpinnerPlaceholder />;
  if (!study) return <NotFound />;

  const kpsFromStudy = study.keyParameters;
  const kpsFromAEs = study.architecturalElements.map(ae => ({
    ...ae,
    dominant: false,
  }));
  const kpsFromOutcrops = study.outcrops.flatMap(oc => oc.keyParameters);

  const keyParameters = [
    ...kpsFromStudy,
    ...(includeAE ? kpsFromAEs : []),
    ...(includeOC ? kpsFromOutcrops : []),
  ];

  const showAEOption = kpsFromAEs.length > 0;

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <KeyParametersPanelV4
          noHeading
          keyParameters={keyParameters}
          dominantGeologyType={study.dominantGeologyType}
          forceShow
        >
          {(kps, tabs) => (
            <>
              <div className="flex justify-between items-end">
                <Heading level={3}>Key Parameters</Heading>
                <div>{tabs}</div>
              </div>

              <Panel>
                <Panel.Body>{kps}</Panel.Body>
                <Panel.Footer className="flex justify-center items-center gap-4">
                  <div className="form-control">
                    <label className="label justify-start cursor-pointer gap-2">
                      <input
                        type="checkbox"
                        className="checkbox checkbox=sm"
                        checked={includeOC}
                        onChange={() => setIncludeOC(v => !v)}
                      />
                      <span className="label-text">Include Outcrop Data</span>
                    </label>
                  </div>
                  {showAEOption && (
                    <div className="form-control">
                      <label className="label justify-start cursor-pointer gap-2">
                        <input
                          type="checkbox"
                          className="checkbox checkbox=sm"
                          checked={includeAE}
                          onChange={() => setIncludeAE(v => !v)}
                        />
                        <span className="label-text">
                          Include Architectural Element Data
                        </span>
                      </label>
                    </div>
                  )}
                </Panel.Footer>
              </Panel>
            </>
          )}
        </KeyParametersPanelV4>
      </div>

      <div>
        <Heading level={3}>Quality Control Parameters</Heading>
        <div className="card shadow">
          <div className="card-body p-4">
            <StudyQualityParametersTable study={study} />
          </div>
        </div>
      </div>
    </div>
  );
}
